import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { getClientCriticalPopulationsMonitoring } from 'api/generated/critical-populations-monitoring/critical-populations-monitoring';
import type { GetClientCriticalPopulationsMonitoringParams } from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { toCriticalElementMonitoringMap } from 'containers/user/critical-elements-monitoring/helpers/monitoring-dto-mappers';
import type {
  CriticalElementMonitoringMap,
  MonitoringDashboardParams,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export const monitoringDashboardMetricsKeys = {
  all: ['monitoringDashboardMetrics'] as const,
  byParams: (params: MonitoringDashboardMetricsParams) =>
    [...monitoringDashboardMetricsKeys.all, params] as const,
};

export type MonitoringDashboardMetricsParams = Omit<
  MonitoringDashboardParams,
  'enteringClass' | 'monitoringYear' | 'cohort'
> & {
  enteringClass: number | null;
  monitoringYear: number | null;
  cohort: string | null;
};

const toRequestParams = (
  params: MonitoringDashboardMetricsParams
): GetClientCriticalPopulationsMonitoringParams => ({
  enteringClassYear: params.enteringClass ?? undefined,
  monitoringYear: params.monitoringYear ?? undefined,
  compareTo: params.compareTo,
  cohort: params.cohort ?? undefined,
  extend: params.extend,
  showClient: params.show.client,
  showCohort: params.show.cohort,
  showVsCohort: params.show.vsCohort,
});

export const useMonitoringDashboardMetrics = (
  params: MonitoringDashboardMetricsParams
): UseQueryResult<CriticalElementMonitoringMap, Error> => {
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId;

  return useQuery({
    queryKey: monitoringDashboardMetricsKeys.byParams(params),
    queryFn: async () => {
      if (
        params.enteringClass === null ||
        params.monitoringYear === null ||
        params.cohort === null
      ) {
        throw new Error(
          'useMonitoringDashboardMetrics: enteringClass, monitoringYear, and cohort are required'
        );
      }

      const dto = await getClientCriticalPopulationsMonitoring(
        userId as string,
        Number(params.clientId),
        toRequestParams(params)
      );

      return toCriticalElementMonitoringMap(dto);
    },
    enabled:
      !!userId &&
      !!params.clientId &&
      params.enteringClass !== null &&
      params.enteringClass > 0 &&
      params.monitoringYear !== null &&
      params.cohort !== null,
  });
};

export default useMonitoringDashboardMetrics;
